import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { PaginationInfo } from '../services/api';

interface PaginationProps {
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({ 
  pagination, 
  onPageChange, 
  onPageSizeChange 
}) => {
  const { 
    current_page, 
    page_size, 
    total_count, 
    total_pages, 
    has_next, 
    has_prev 
  } = pagination;

  // 生成頁碼按鈕
  const generatePageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;
    
    if (total_pages <= maxVisiblePages) {
      // 如果總頁數小於等於最大顯示頁數，顯示所有頁碼
      for (let i = 1; i <= total_pages; i++) {
        pages.push(i);
      }
    } else {
      // 複雜的分頁邏輯
      const halfVisible = Math.floor(maxVisiblePages / 2);
      let startPage = Math.max(1, current_page - halfVisible);
      let endPage = Math.min(total_pages, current_page + halfVisible);
      
      // 調整起始和結束頁碼
      if (endPage - startPage < maxVisiblePages - 1) {
        if (startPage === 1) {
          endPage = Math.min(total_pages, startPage + maxVisiblePages - 1);
        } else {
          startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }
      }
      
      // 添加第一頁和省略號
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
          pages.push('...');
        }
      }
      
      // 添加中間頁碼
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // 添加省略號和最後一頁
      if (endPage < total_pages) {
        if (endPage < total_pages - 1) {
          pages.push('...');
        }
        pages.push(total_pages);
      }
    }
    
    return pages;
  };

  const pageNumbers = generatePageNumbers();

  return (
    <div className="bg-white px-3 sm:px-4 py-3 border-t border-gray-200">
      {/* 手機版分頁 */}
      <div className="sm:hidden">
        {/* 頁面資訊 */}
        <div className="flex justify-center items-center mb-3">
          <p className="text-xs text-gray-600">
            第 {current_page} 頁 / 共 {total_pages} 頁 ({total_count} 筆記錄)
          </p>
        </div>
        
        {/* 分頁控制 */}
        <div className="flex justify-between items-center space-x-2">
          <button
            onClick={() => onPageChange(1)}
            disabled={current_page === 1}
            className="flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-w-[44px]"
          >
            <ChevronsLeft className="h-4 w-4" />
          </button>
          
          <button
            onClick={() => onPageChange(current_page - 1)}
            disabled={!has_prev}
            className="flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            上一頁
          </button>
          
          {/* 頁碼輸入 */}
          <div className="flex items-center space-x-2">
            <input
              type="number"
              min="1"
              max={total_pages}
              value={current_page}
              onChange={(e) => {
                const page = parseInt(e.target.value);
                if (page >= 1 && page <= total_pages) {
                  onPageChange(page);
                }
              }}
              className="w-16 px-2 py-1 text-center border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <button
            onClick={() => onPageChange(current_page + 1)}
            disabled={!has_next}
            className="flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            下一頁
            <ChevronRight className="h-4 w-4 ml-1" />
          </button>
          
          <button
            onClick={() => onPageChange(total_pages)}
            disabled={current_page === total_pages}
            className="flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-w-[44px]"
          >
            <ChevronsRight className="h-4 w-4" />
          </button>
        </div>
        
        {/* 每頁筆數選擇 */}
        <div className="flex justify-center items-center mt-3 space-x-2">
          <label htmlFor="pageSizeMobile" className="text-xs text-gray-700">
            每頁:
          </label>
          <select
            id="pageSizeMobile"
            value={page_size}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>
      
      {/* 桌面版分頁 */}
      <div className="hidden sm:flex sm:items-center sm:justify-between">
        <div className="flex items-center space-x-4">
          <p className="text-sm text-gray-700">
            顯示第 <span className="font-medium">{(current_page - 1) * page_size + 1}</span> 到{' '}
            <span className="font-medium">
              {Math.min(current_page * page_size, total_count)}
            </span>{' '}
            筆，共 <span className="font-medium">{total_count}</span> 筆
          </p>
          
          <div className="flex items-center space-x-2">
            <label htmlFor="pageSize" className="text-sm text-gray-700">
              每頁顯示:
            </label>
            <select
              id="pageSize"
              value={page_size}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={10}>10 筆</option>
              <option value={20}>20 筆</option>
              <option value={50}>50 筆</option>
              <option value={100}>100 筆</option>
            </select>
          </div>
        </div>
        
        <div>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            {/* 第一頁按鈕 */}
            <button
              onClick={() => onPageChange(1)}
              disabled={current_page === 1}
              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronsLeft className="h-4 w-4" />
            </button>
            
            {/* 上一頁按鈕 */}
            <button
              onClick={() => onPageChange(current_page - 1)}
              disabled={!has_prev}
              className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            
            {/* 頁碼按鈕 */}
            {pageNumbers.map((page, index) => (
              <React.Fragment key={index}>
                {page === '...' ? (
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    ...
                  </span>
                ) : (
                  <button
                    onClick={() => onPageChange(page as number)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      current_page === page
                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                )}
              </React.Fragment>
            ))}
            
            {/* 下一頁按鈕 */}
            <button
              onClick={() => onPageChange(current_page + 1)}
              disabled={!has_next}
              className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            
            {/* 最後一頁按鈕 */}
            <button
              onClick={() => onPageChange(total_pages)}
              disabled={current_page === total_pages}
              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronsRight className="h-4 w-4" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default Pagination; 